import React, { useMemo, useCallback, useState } from 'react';
import { makeStyles, IconButton, TextField, Typography, Select, MenuItem, Tooltip } from '@material-ui/core';
import { Period, PeriodTypeToString } from './SessionPeriods';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import DeleteIcon from '@material-ui/icons/Delete';
import TimeInputField from '../Util/TimeInputField';
import ArrowDropDownCircleSharpIcon from '@material-ui/icons/ArrowDropDownCircleSharp';
import PlayCircleFilledIcon from '@material-ui/icons/PlayCircleFilled';
import { PeriodType } from '../../api';
import AreYouSure from '../Util/AreYouSure';
import { secondsToTimeSpan, timeSpanToSeconds } from '../../services/sessionModels';

const useStyles = makeStyles({
  topRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontWeight: 'bold',
    fontSize: '16px',
    height: '5%',
  },
  periodList: {
    overflowY: 'scroll',
    display: 'flex',
    height: '92%',
    flexDirection: 'column',
    padding: '0px 12px 12px 0px',
    marginTop: '12px',
  },
  periodItem: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '210px',
    backgroundColor: '#eee',
    marginBottom: '8px',
    padding: '8px',
  },
  periodItemRow: {
    padding: '4px',
    display: 'flex',
    flexDirection: 'row',
    fontSize: '10px',
    justifyContent: 'center',
    verticalAlign: 'center',
    height: '30px',
  },
  periodItemColumn: {
    width: '33%',
  },
  columnTitle: {
    fontSize: 12,
    padding: '6px 12px 0px 0px',
    float: 'right',
  },
  deleteIcon: {
    display: 'inline',
    marginLeft: 'auto',
    marginRight: 0,
    color: '#000',
  },
  textField: {
    paddingTop: 0,
  },
  textFieldInput: {
    fontSize: '12px',
  },
  selectBox: {
    marginLeft: 'auto',
    marginRight: 0,
    width: '80%',
  },
  timeTextField: {
    fontSize: '12px',
    fontWeight: 'bold',
    '& .Mui-disabled': {
      color: '#000',
      fontWeight: 'normal',
    },
  },
  icon: {
    display: 'inline',
    color: '#000',
  },
  startIcon: {
    display: 'inline',
    color: '#090',
  },
  endIcon: {
    display: 'inline',
    color: '#900',
  },
});

type Props = {
  periods: Period[];
  addPeriod: () => void;
  editPeriod: (period: Period) => void;
  deletePeriod: (period: Period) => void;
  playTime: number;
  seekToPlayTime: (time: number) => void;
  hasExistingLabelPoints: boolean;
};

const SessionPeriodList = (props: Props) => {
  const classes = useStyles();

  const { periods, addPeriod, editPeriod, deletePeriod, playTime, seekToPlayTime, hasExistingLabelPoints } = props;

  const [isChangingStartTime, setIsChangingStartTime] = useState<boolean>(false);
  const [changePeriod, setChangePeriod] = useState<Period | undefined>(undefined);
  const [changeNewTime, setChangeNewTime] = useState<number>(0);

  const updateLabel = useCallback(
    (period: Period, newLabel: string) => {
      period.label = newLabel;
      editPeriod(period);
    },
    [editPeriod]
  );

  const updateType = useCallback(
    (period: Period, newType: string) => {
      period.type = newType;
      editPeriod(period);
    },
    [editPeriod]
  );

  const updateStart = useCallback(
    (period: Period, newTime: number) => {
      period.startTime = secondsToTimeSpan(newTime);
      if (period.endTime && newTime > timeSpanToSeconds(period.endTime)) {
        period.endTime = secondsToTimeSpan(newTime);
      }
      editPeriod(period);
    },
    [editPeriod]
  );

  const tryUpdateStart = useCallback(
    (period: Period, newTime: number) => {
      if (hasExistingLabelPoints) {
        setIsChangingStartTime(true);
        setChangePeriod(period);
        setChangeNewTime(newTime);
      } else {
        updateStart(period, newTime);
      }
    },
    [hasExistingLabelPoints, updateStart]
  );

  const onConfirmUpdateStart = useCallback(() => {
    if (changePeriod !== undefined) {
      updateStart(changePeriod, changeNewTime);
      setIsChangingStartTime(false);
      setChangeNewTime(0);
      setChangePeriod(undefined);
    }
  }, [changeNewTime, changePeriod, updateStart]);

  const onCancelUpdateStart = useCallback(() => {
    setIsChangingStartTime(false);
    setChangeNewTime(0);
    setChangePeriod(undefined);
  }, []);

  const updateEnd = useCallback(
    (period: Period, newTime: number) => {
      period.endTime = secondsToTimeSpan(newTime);
      editPeriod(period);
    },
    [editPeriod]
  );

  const periodItems = useMemo(() => {
    return periods.map((period: Period) => {
      const value = { ...period };
      return (
        <div className={classes.periodItem} key={value.id}>
          <div className={classes.periodItemRow}>
            <Select
              className={classes.selectBox}
              value={value.type || PeriodType.Other}
              onChange={(e) => updateType(value, e.target.value as string)}
            >
              <MenuItem value={PeriodType.FirstHalf}>{PeriodTypeToString(PeriodType.FirstHalf)}</MenuItem>
              <MenuItem value={PeriodType.SecondHalf}>{PeriodTypeToString(PeriodType.SecondHalf)}</MenuItem>
              <MenuItem value={PeriodType.ExtraTimeFirstHalf}>
                {PeriodTypeToString(PeriodType.ExtraTimeFirstHalf)}
              </MenuItem>
              <MenuItem value={PeriodType.ExtraTimeSecondHalf}>
                {PeriodTypeToString(PeriodType.ExtraTimeSecondHalf)}
              </MenuItem>
              <MenuItem value={PeriodType.Penalties}>{PeriodTypeToString(PeriodType.Penalties)}</MenuItem>
              <MenuItem value={PeriodType.Other}>{PeriodTypeToString(PeriodType.Other)}</MenuItem>
            </Select>

            <Tooltip title="Delete Period" arrow>
              <IconButton className={classes.deleteIcon} size="small" onClick={() => deletePeriod(value)}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </div>

          <div className={classes.periodItemRow}>
            <div className={classes.periodItemColumn}>
              <Typography className={classes.columnTitle}>Label</Typography>
            </div>
            <div className={classes.periodItemColumn}>
              <TextField
                className={classes.textField}
                defaultValue={value.label || ''}
                onChange={(e) => updateLabel(value, e.currentTarget.value)}
                InputProps={{
                  classes: {
                    input: classes.textFieldInput,
                  },
                }}
              />
            </div>
            <div className={classes.periodItemColumn}></div>
          </div>

          <div className={classes.periodItemRow}>
            <div className={classes.periodItemColumn}>
              <Typography className={classes.columnTitle}>Start</Typography>
            </div>
            <div className={classes.periodItemColumn}>
              <TimeInputField
                className={classes.timeTextField}
                timeSeconds={timeSpanToSeconds(value.startTime)}
                onTimeChanged={(newTime: number) => {
                  tryUpdateStart(value, newTime);
                }}
              />
            </div>
            <div className={classes.periodItemColumn}>
              <Tooltip title="Set current video time as start time" arrow>
                <IconButton
                  className={classes.startIcon}
                  size="small"
                  onClick={() => {
                    tryUpdateStart(value, playTime);
                  }}
                >
                  <ArrowDropDownCircleSharpIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Go to start time" arrow>
                <IconButton
                  disabled={!value.startTime}
                  className={classes.icon}
                  size="small"
                  onClick={() => {
                    seekToPlayTime(timeSpanToSeconds(value.startTime));
                  }}
                >
                  <PlayCircleFilledIcon />
                </IconButton>
              </Tooltip>
            </div>
          </div>

          <div className={classes.periodItemRow}>
            <div className={classes.periodItemColumn}>
              <Typography className={classes.columnTitle}>End</Typography>
            </div>
            <div className={classes.periodItemColumn}>
              <TimeInputField
                className={classes.timeTextField}
                timeSeconds={timeSpanToSeconds(value.endTime)}
                onTimeChanged={(newTime: number) => {
                  updateEnd(value, newTime);
                }}
              />
            </div>
            <div className={classes.periodItemColumn}>
              <Tooltip title="Set current video time as end time" arrow>
                <IconButton
                  className={classes.endIcon}
                  size="small"
                  onClick={() => {
                    updateEnd(value, playTime);
                  }}
                >
                  <ArrowDropDownCircleSharpIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Go to end time" arrow>
                <IconButton
                  disabled={!value.endTime}
                  className={classes.icon}
                  size="small"
                  onClick={() => {
                    seekToPlayTime(timeSpanToSeconds(value.endTime));
                  }}
                >
                  <PlayCircleFilledIcon />
                </IconButton>
              </Tooltip>
            </div>
          </div>

          <div className={classes.periodItemRow}>
            <div className={classes.periodItemColumn}>
              <Typography className={classes.columnTitle}>Duration</Typography>
            </div>
            <div className={classes.periodItemColumn}>
              <TimeInputField
                className={classes.timeTextField}
                timeSeconds={Math.max(0, timeSpanToSeconds(value.endTime) - timeSpanToSeconds(value.startTime))}
              />
            </div>
            <div className={classes.periodItemColumn}></div>
          </div>
        </div>
      );
    });
  }, [classes, playTime, deletePeriod, periods, updateLabel, updateType, tryUpdateStart, updateEnd, seekToPlayTime]);

  return (
    <>
      <div className={classes.topRow}>
        <span>Periods</span>
        <span>
          <Tooltip title="Add a new period" arrow>
            <IconButton onClick={addPeriod}>
              <AddCircleIcon color="primary" style={{ fontSize: 32 }} />
            </IconButton>
          </Tooltip>
        </span>
      </div>
      <div className={classes.periodList}>{periodItems}</div>
      <AreYouSure
        open={isChangingStartTime}
        title="WARNING! Changing period start time."
        text="You have labeled points set for this session's Team Specification step. Changing the start time of this period will remove ALL points labeled! Are you sure?!"
        onNo={onCancelUpdateStart}
        onYes={onConfirmUpdateStart}
      />
    </>
  );
};

export default SessionPeriodList;
